/**
 * `ResultCard` — the combat **result card**: a pure render of a resolution's recorded
 * `ResolvedStep[]` and `about` breadcrumb, through the shared narrator ({@link narrateResolution}).
 *
 * **Pure render, never re-derivation (the load-bearing rule).** Every number shown is read straight
 * off a recorded step; the card never re-rolls, re-mitigates, or recomputes hit-vs-miss. The
 * narrator does the formatting and is shared with the CLI, so the card and `lvtt` say the same
 * thing.
 *
 * Two layouts, one narrator:
 *  - **An attack** (a `to_hit` or `miss_chance` in the list) renders as {@link AttackCard}
 *    (`comms-attack-reaction-cards` Phase 3): the glance ({@link summarizeAttackGlance}) over the
 *    narrator's story and structured receipt. `CommsApp` groups a multi-shot action's entries into
 *    one {@link AttackGroupCard} on top of the same views.
 *  - **Anything else** (a Search, a thrown grenade, a mine going off, the GM's damage) renders as
 *    {@link EventCard}: the narrator's story line over the same receipt grid.
 *
 * There is no ledger. It rendered every resolution the narrator declined, and since
 * `narration-ledger-retirement` Phase 3 the narrator declines nothing.
 */
import type { ResolutionAbout, ResolvedStep } from "@lancer-vtt/domain";
import { narrateResolution, summarizeAttackGlance } from "@lancer-vtt/rules";
import { AttackCard, EventCard, type ShotView } from "./AttackCard";

/** Resolve a combatant id to a display name (falls back to the id). */
export type NameOf = (id: string) => string;

/** One resolution as the COMMS card reads it — the fields a bridged feed entry carries. */
export type ResolutionInput = {
  steps: readonly ResolvedStep[];
  nameOf?: NameOf;
  kind?: string;
  about?: ResolutionAbout;
  warnings?: readonly string[];
};

/**
 * The attack card's view of one resolution: its glance, plus the narrator's story and structured
 * receipt behind it. `undefined` when the resolution holds no attack roll — that one renders as an
 * {@link EventCard}.
 */
export function buildShotView({ steps, nameOf, kind, about, warnings }: ResolutionInput): ShotView | undefined {
  const glance = summarizeAttackGlance({ steps, ...(about !== undefined ? { about } : {}), ...(nameOf !== undefined ? { names: nameOf } : {}) });
  if (glance === undefined) return undefined;
  const narrated = narrateResolution({ steps, kind, about, names: nameOf, structured: true });
  return {
    glance,
    story: narrated.story,
    receipt: narrated.receipt,
    ...(warnings !== undefined && warnings.length > 0 ? { warnings } : {}),
    ...(about?.reaction === "overwatch" ? { overwatch: true } : {}),
    ...(about?.forced === true ? { forced: true } : {}),
  };
}

/**
 * Render one resolution: an {@link AttackCard} when it holds an attack roll, else an
 * {@link EventCard}. `warnings`, when non-empty, render in their own section below either
 * (`decisions/0113`: advisory notes read as different from facts).
 */
export function ResultCard({ steps, nameOf, kind, about, warnings }: ResolutionInput) {
  const view = buildShotView({ steps, nameOf, kind, about, warnings });
  if (view !== undefined) return <AttackCard view={view} />;
  const narrated = narrateResolution({ steps, kind, about, names: nameOf, structured: true });
  return <EventCard story={narrated.story} receipt={narrated.receipt} warnings={warnings} />;
}
